import { FC } from 'react';
import { Content } from '@prismicio/client';
import {
  JSXMapSerializer,
  PrismicRichText,
  SliceComponentProps,
} from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import { ArrowRightIcon } from '@/assets';

/**
 * Props for `ImpactHighlights`.
 */
export type ImpactHighlightsProps =
  SliceComponentProps<Content.ImpactHighlightsSlice>;

const customRichTextComponents: JSXMapSerializer = {
  listItem: ({ children }) => (
    <li className="flex items-start mb-2">
      {/* Green horizontal line bullet */}
      <div className="w-3 h-0.5 bg-[#05F273] mt-2 mr-2 flex-shrink-0"></div>
      <p className="text-white/80">{children}</p>
    </li>
  ),
  // Default paragraph styling if impact_list contains paragraphs
  paragraph: ({ children }) => <p className="text-white/80">{children}</p>,
};

/**
 * Component for "ImpactHighlights" Slices.
 */
const ImpactHighlights: FC<ImpactHighlightsProps> = ({ slice }) => {
  const {
    title,
    subtitle,
    impact_cards: impactCards = [],
  } = slice.primary || {};

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-white pb-12 md:pb-28 pt-8 md:pt-24 bg-[#0F423A]"
    >
      <div className="container mx-auto px-4 md:px-6 flex flex-col items-start text-start gap-12">
        <div className="flex flex-col gap-6">
          <PrismicRichText
            field={title}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-4xl md:text-5xl font-bold">{children}</h2>
              ),
            }}
          />
          <PrismicRichText
            field={subtitle}
            components={{
              paragraph: ({ children }) => (
                <p className="text-lg text-white/80">{children}</p>
              ),
            }}
          />
          {slice.variation === 'default' && slice.primary.cta && (
            <div className="flex items-center gap-4">
              {slice.primary.cta && (
                <PrismicNextLink
                  field={slice.primary.cta}
                  className="secondary-button"
                >
                  {slice.primary.cta.text
                    ? slice.primary.cta.text
                    : 'Learn More'}
                  <ArrowRightIcon className="arrow-icon" />
                </PrismicNextLink>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
            {impactCards.map((card, index) => (
              <div key={index} className="bg-white/5 p-6 rounded-2xl">
                <p
                  className="text-xl font-semibold mb-4"
                  style={{ color: '#05F273' }}
                >
                  {card.card_title}
                </p>
                <PrismicRichText
                  field={card.impact_list}
                  components={customRichTextComponents}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactHighlights;
